import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { userMaySeeActivitiesImportNav } from "../lib/adminImport";
import { fetchAdminRegionalCalendarsOverview, reviewCalendarSubmission } from "../api/adminRegionalCalendars";
import "./AdminRegionalCalendarsPage.css";

function statusLabel(status) {
  if (status === "stale") return "Veraltet";
  if (status === "attention") return "Prüfen";
  return "OK";
}

function formatDeWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-CH", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function formatDeDate(ymd) {
  if (!ymd) return "—";
  const [y, m, d] = ymd.split("-");
  return `${d}.${m}.${y}`;
}

function PackCard({ pack, onRevert, busy }) {
  const canRevert = pack.generation > 1;
  return (
    <li className={`admin-regional-pack admin-regional-pack--${pack.status}`}>
      <div className="admin-regional-pack-head">
        <strong>
          {pack.label} ({pack.jurisdiction})
        </strong>
        <span className={`admin-regional-status admin-regional-status--${pack.status}`}>
          {statusLabel(pack.status)}
        </span>
      </div>
      <dl className="admin-regional-meta">
        <div>
          <dt>Schuljahre</dt>
          <dd>{pack.schoolYears?.length ? pack.schoolYears.join(", ") : "—"}</dd>
        </div>
        <div>
          <dt>Abdeckung</dt>
          <dd>
            {formatDeDate(pack.coverage?.from)} – {formatDeDate(pack.coverage?.to)}
          </dd>
        </div>
        <div>
          <dt>Einträge</dt>
          <dd>
            {pack.counts?.publicHoliday ?? 0} Feiertage, {pack.counts?.schoolHoliday ?? 0}{" "}
            Schulferien
          </dd>
        </div>
        {pack.inherits?.length ? (
          <div>
            <dt>Erbt</dt>
            <dd>{pack.inherits.join(", ")}</dd>
          </div>
        ) : null}
        <div>
          <dt>Zuletzt ingestiert</dt>
          <dd>{formatDeWhen(pack.lastIngestedAt)}</dd>
        </div>
        <div>
          <dt>Nächstes Ereignis</dt>
          <dd>
            {pack.nextEvent
              ? `${pack.nextEvent.name} (${formatDeDate(pack.nextEvent.startsOn)})`
              : "—"}
          </dd>
        </div>
      </dl>
      {pack.sourceUrl ? (
        <p className="admin-regional-source">
          Quelle:{" "}
          <a href={pack.sourceUrl} target="_blank" rel="noreferrer">
            {pack.sourceUrl}
          </a>
        </p>
      ) : null}
      {pack.issues?.length ? (
        <ul className="admin-regional-issues">
          {pack.issues.map((issue) => (
            <li key={issue}>{issue}</li>
          ))}
        </ul>
      ) : null}
      {canRevert ? (
        <button
          type="button"
          className="admin-regional-refresh"
          disabled={busy}
          onClick={() => onRevert(pack.jurisdiction)}
        >
          Zurücksetzen
        </button>
      ) : null}
    </li>
  );
}

export default function AdminRegionalCalendarsPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const loadOverview = useCallback(async () => {
    setOverviewError(null);
    setOverviewLoading(true);
    try {
      const data = await fetchAdminRegionalCalendarsOverview();
      setOverview(data);
    } catch (err) {
      setOverview(null);
      setOverviewError(err instanceof Error ? err.message : String(err));
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  const runReview = useCallback(async (body) => {
    setBusyId(body.submissionId || body.packId || "review");
    setOverviewError(null);
    try {
      await reviewCalendarSubmission(body);
      await loadOverview();
    } catch (err) {
      setOverviewError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }, [loadOverview]);

  useEffect(() => {
    if (!loading && user && !userMaySeeActivitiesImportNav(user)) {
      navigate("/mehr", { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user && userMaySeeActivitiesImportNav(user)) {
      loadOverview();
    }
  }, [user, loadOverview]);

  if (loading) {
    return (
      <div className="admin-regional-page">
        <p className="admin-regional-muted">Laden…</p>
      </div>
    );
  }

  if (!user || !userMaySeeActivitiesImportNav(user)) {
    return null;
  }

  const usage = overview?.usage?.byJurisdiction || {};
  const usageEntries = Object.entries(usage).sort((a, b) => b[1] - a[1]);

  return (
    <div className="admin-regional-page">
      <header className="admin-regional-header">
        <h2 className="admin-regional-title">Regionale Kalender (Admin)</h2>
        <p className="admin-regional-lead">
          Ferien- und Feiertags-Packs. Kanton und Stadt kommen aus der offiziellen Quelle.
          Schulen und Klassen erscheinen hier, nachdem ein Vorschlag veröffentlicht wurde.
          Dafür ist kein Deploy nötig.
        </p>
        <Link to="/mehr" className="admin-regional-back">
          ← Mehr
        </Link>
      </header>

      <section className="admin-regional-overview" aria-labelledby="admin-regional-summary">
        <div className="admin-regional-overview-head">
          <h3 id="admin-regional-summary" className="admin-regional-section-title">
            Status
          </h3>
          <button
            type="button"
            className="admin-regional-refresh"
            onClick={loadOverview}
            disabled={overviewLoading}
          >
            {overviewLoading ? "Lädt…" : "Aktualisieren"}
          </button>
        </div>

        {overviewError ? <p className="admin-regional-error">{overviewError}</p> : null}

        {overview ? (
          <>
            <p className="admin-regional-summary">
              Standard: <code>{overview.defaultJurisdiction}</code> — Gesamt:{" "}
              <strong>{statusLabel(overview.overallStatus)}</strong>
              {overview.checkedAt ? ` (Stand ${formatDeWhen(overview.checkedAt)})` : null}
            </p>
            <p className="admin-regional-summary">
              Refresh-Saison: Monat {overview.refreshSeason?.startMonth}–
              {overview.refreshSeason?.endMonth}
            </p>
          </>
        ) : null}

        <ul className="admin-regional-pack-list">
          {(overview?.packs || []).map((pack) => (
            <PackCard
              key={pack.jurisdiction}
              pack={pack}
              busy={busyId === pack.jurisdiction}
              onRevert={(packId) => runReview({ action: "revert", packId })}
            />
          ))}
        </ul>

        <h4 className="admin-regional-subsection">Vorschläge</h4>
        {(overview?.submissions || []).length ? (
          <ul className="admin-regional-pack-list">
            {overview.submissions.map((submission) => (
              <li key={submission.id} className="admin-regional-pack">
                <div className="admin-regional-pack-head">
                  <strong>
                    {submission.label} ({submission.scope})
                  </strong>
                  <span>{submission.confirmations} Familie{submission.confirmations === 1 ? "" : "n"}</span>
                </div>
                <p className="admin-regional-source">
                  {submission.eventCount} Termine
                  {submission.institutionType ? ` · ${submission.institutionType}` : ""}
                  {submission.added?.length ? ` · neu: ${submission.added.join(", ")}` : ""}
                  {submission.removed?.length ? ` · weg: ${submission.removed.join(", ")}` : ""}
                </p>
                <div className="admin-regional-actions">
                  <button
                    type="button"
                    className="admin-regional-refresh"
                    disabled={busyId === submission.id}
                    onClick={() =>
                      runReview({
                        action: "accept",
                        submissionId: submission.id,
                        institutionType: submission.institutionType || undefined,
                      })
                    }
                  >
                    Veröffentlichen
                  </button>
                  <button
                    type="button"
                    className="admin-regional-refresh"
                    disabled={busyId === submission.id}
                    onClick={() => runReview({ action: "reject", submissionId: submission.id })}
                  >
                    Ablehnen
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="admin-regional-muted">Keine offenen Vorschläge.</p>
        )}

        {usageEntries.length ? (
          <>
            <h4 className="admin-regional-subsection">Haushalte nach Jurisdiction</h4>
            <ul className="admin-regional-usage">
              {usageEntries.map(([jurisdiction, count]) => (
                <li key={jurisdiction}>
                  <code>{jurisdiction}</code>: {count}
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </section>
    </div>
  );
}
